import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthActions } from "@/components/AuthProvider";
import { updateRegistrationInfo } from "@/server/auth";
import { toastManager } from "@/components/ui/toast";
import { formatPrice } from "@/utils/currencyUtils";
import { MaskedDateInput } from "@/components/ui/maskedDateInput";
import { isMinimumAge } from "@/utils/ageUtils";
import { parseDateInput } from "@/utils/dateInputUtils";
import type { Profile } from "@/apiTypes/profile";

interface RegistrationFlowModalProps {
  /** Current user profile — used to determine which fields are missing. */
  profile: Profile;
  /** When true, the liability waiver field is not required. */
  skipRiderFields?: boolean;
  eventName: string;
  /**
   * Name of the user-selected category. Pass `undefined` for events where
   * the user had only one category to choose from — the modal will then
   * omit the Category summary row entirely. Same shape as `routeName`.
   */
  categoryName?: string;
  /**
   * Name of the user-selected route. Pass `undefined` for events with
   * a single route (or none) — the modal omits the Route summary row.
   */
  routeName?: string;
  categoryPrice?: number;
  /** Prerequisites the user has not yet satisfied. */
  unsatisfiedPrerequisites?: Array<{ id: string; name: string }>;
  onConfirm: () => void;
  onClose: () => void;
}

/** Checks which registration fields are still missing from the profile. */
function getMissingFields(profile: Profile) {
  return {
    name: !profile.firstName || !profile.lastName,
    dateOfBirth: !profile.dateOfBirth,
    waiver: !profile.liabilityWaiverAcceptedAt,
  };
}

/**
 * Multi-step modal for the deep-link registration flow.
 * Step 1 (conditional): Collects only the missing profile fields.
 * Step 2: Shows a confirmation of what the user is registering for.
 */
export const RegistrationFlowModal: FC<RegistrationFlowModalProps> = ({
  profile,
  skipRiderFields = false,
  eventName,
  categoryName,
  routeName,
  categoryPrice,
  unsatisfiedPrerequisites = [],
  onConfirm,
  onClose,
}) => {
  const { invalidate } = useAuthActions();
  const allMissing = getMissingFields(profile);
  const missing = skipRiderFields
    ? { ...allMissing, waiver: false }
    : allMissing;
  const needsProfileInfo =
    missing.name || missing.dateOfBirth || missing.waiver;

  const [step, setStep] = useState<"profile" | "confirm">(
    needsProfileInfo ? "profile" : "confirm",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [dobError, setDobError] = useState<string | null>(null);

  const handleProfileSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);

      const firstName = missing.name
        ? (formData.get("firstName") as string).trim()
        : profile.firstName!;
      const lastName = missing.name
        ? (formData.get("lastName") as string).trim()
        : profile.lastName!;
      const dateOfBirth = missing.dateOfBirth
        ? parseDateInput((formData.get("dateOfBirth") as string).trim())
        : new Date(profile.dateOfBirth!);

      if (!firstName || !lastName) {
        toastManager.add({
          title: "Please fill in all fields",
          type: "error",
        });
        return;
      }

      if (!dateOfBirth) {
        setDobError("Please enter a valid date (MM/DD/YYYY).");
        return;
      }

      if (!isMinimumAge(dateOfBirth)) {
        setDobError(
          "You must be at least 18 years old to register for events.",
        );
        return;
      }
      setDobError(null);

      if (missing.waiver && !waiverAccepted) {
        toastManager.add({
          title: "Please accept the liability waiver",
          type: "error",
        });
        return;
      }

      setIsSubmitting(true);
      try {
        await updateRegistrationInfo({
          data: {
            firstName,
            lastName,
            dateOfBirth,
            ...(!skipRiderFields && { liabilityWaiverAccepted: true }),
          },
        });
        await invalidate();
        setStep("confirm");
      } catch (err) {
        toastManager.add({
          title: err instanceof Error ? err.message : "Failed to save info",
          type: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [waiverAccepted, invalidate, missing, profile],
  );

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  }, [onConfirm]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="presentation"
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold font-sans text-gray-900">
            {step === "profile"
              ? "Complete Your Profile"
              : "Confirm Registration"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "profile" ? (
          <form onSubmit={handleProfileSubmit} className="p-5 space-y-4">
            <p className="text-sm text-gray-600">
              We need a few details before you can register for events.
            </p>

            {missing.name && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName" className="mb-1.5">
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    required
                    placeholder="First name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="mb-1.5">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    required
                    placeholder="Last name"
                  />
                </div>
              </div>
            )}

            {missing.dateOfBirth && (
              <div>
                <Label htmlFor="dateOfBirth" className="mb-1.5">
                  Date of Birth
                </Label>
                <MaskedDateInput id="dateOfBirth" name="dateOfBirth" required />
                {dobError && (
                  <p className="text-xs text-red-600 mt-1.5">{dobError}</p>
                )}
              </div>
            )}

            {missing.waiver && (
              <div className="border-t border-gray-200 pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={waiverAccepted}
                    onChange={(e) => setWaiverAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">
                    I acknowledge that cycling activities involve inherent
                    risks, and I accept full responsibility for my
                    participation. I waive any liability claims against event
                    organizers for injuries sustained during events.
                  </span>
                </label>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting || (missing.waiver && !waiverAccepted)}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11 text-sm font-medium"
            >
              {isSubmitting && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {isSubmitting ? "Saving..." : "Continue"}
            </Button>
          </form>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600">
              You are about to register for:
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Event</span>
                <span className="text-sm font-semibold text-gray-900">
                  {eventName}
                </span>
              </div>
              {routeName && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Route</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {routeName}
                  </span>
                </div>
              )}
              {categoryName && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Category</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {categoryName}
                  </span>
                </div>
              )}
              {categoryPrice !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Price</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatPrice(categoryPrice)}
                  </span>
                </div>
              )}
            </div>

            {unsatisfiedPrerequisites.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-2.5 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-semibold text-amber-900">
                    Missing Requirement
                  </p>
                </div>
                <p className="text-sm text-amber-800 mb-2">
                  {unsatisfiedPrerequisites.length === 1 ? (
                    <>
                      Our records don&apos;t show that you&apos;ve completed{" "}
                      <strong>{unsatisfiedPrerequisites[0].name}</strong>, which
                      is required to register for this event.
                    </>
                  ) : (
                    <>
                      Our records don&apos;t show that you&apos;ve completed all
                      required events for this registration.
                    </>
                  )}
                </p>
                <p className="text-sm text-amber-800">
                  <strong>Completed it elsewhere?</strong> You can continue with
                  registration, but the organizer will need to manually review
                  and approve your entry before it&apos;s confirmed.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white h-11 text-sm font-medium"
              >
                {isSubmitting && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {isSubmitting
                  ? "Registering..."
                  : unsatisfiedPrerequisites.length > 0
                    ? "Register Anyway"
                    : "Confirm"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
